import { LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { useVaultFileDownloads } from "../../hooks/use-vault-file-downloads";

export function VaultDownloadFeedback({
  downloads,
}: {
  downloads: ReturnType<typeof useVaultFileDownloads>;
}) {
  return (
    <>
      {downloads.isBusy && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-primary"
        >
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          {downloads.progress
            ? `Preparing files (${downloads.progress.completed} of ${downloads.progress.total})…`
            : "Preparing download…"}
        </p>
      )}
      {downloads.failures.length > 0 && (
        <div
          role="alert"
          className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <p className="font-semibold">
            {downloads.failures.length} file
            {downloads.failures.length === 1 ? "" : "s"} could not be
            downloaded.
          </p>
          <ul className="max-h-28 space-y-1 overflow-y-auto text-xs">
            {downloads.failures.map(({ file, message }) => (
              <li key={file.id} className="break-words">
                {file.fileName}: {message}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={downloads.isBusy}
              onClick={() => void downloads.retryFailed()}
            >
              Retry failed files
            </Button>
            {downloads.successfulCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={downloads.isBusy}
                onClick={() => void downloads.downloadSuccessful()}
              >
                Download successful ({downloads.successfulCount})
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={downloads.isBusy}
              onClick={downloads.dismissFailures}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {!downloads.isBusy &&
        downloads.failures.length === 0 &&
        downloads.successfulCount > 0 && (
          <div
            role="status"
            className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <p>Your files are ready, but the download did not finish.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void downloads.downloadSuccessful()}
            >
              Download prepared files ({downloads.successfulCount})
            </Button>
          </div>
        )}
    </>
  );
}
